import { z } from 'zod'

import { momentInZone } from '@/lib/time/zonedTime.ts'

/** Minutes since midnight, local time. */
const timeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM')

export const weekdaySchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
])

export type Weekday = z.infer<typeof weekdaySchema>

export const workingDaySchema = z.object({
  weekday: weekdaySchema,
  /** Absent means the business is closed that day. */
  opensAt: timeSchema.optional(),
  closesAt: timeSchema.optional(),
})

function isKnownTimeZone(zone: string): boolean {
  try {
    // Throws RangeError for a zone Intl does not know.
    return Intl.DateTimeFormat('en-US', { timeZone: zone }).resolvedOptions().timeZone !== ''
  } catch {
    return false
  }
}

export const businessSchema = z.object({
  name: z.string().min(1),
  heroUrl: z.string().optional(),
  logoUrl: z.string().optional(),
  /** Brand accent as a hex pair [light, dark]. Astryx derives the whole accent
   *  scale from it, including a contrast-safe --color-on-accent, so no
   *  per-tenant colour is ever hand-written. */
  accent: z.tuple([
    z.string().regex(/^#[0-9a-fA-F]{6}$/),
    z.string().regex(/^#[0-9a-fA-F]{6}$/),
  ]),
  tagline: z.string().trim(),
  about: z.string().trim(),
  address: z.string().min(1),
  /**
   * May be empty. The column defaults to '' and the pipeline's own contract
   * (`tenantConfig.ts`) defaults it to '' too. This used to be `.min(1)`, which
   * nothing noticed while the only data was hand-written fixtures that all had
   * a note: against a real database, every studio without one failed
   * validation and its home screen never loaded.
   */
  addressNote: z.string(),
  /** Digits and a leading plus only — used to build a `tel:` href. */
  phone: z.string().regex(/^\+\d{10,15}$/, 'Expected an E.164 phone number'),
  phoneDisplay: z.string().min(1),
  workingDays: z.array(workingDaySchema).length(7),
  /**
   * IANA zone the studio keeps its hours in. Every time shown to a customer or
   * an owner is on this clock, never the browser's. Checked against Intl so a
   * typo fails here rather than as a wrong hour on screen.
   */
  timezone: z.string().refine(isKnownTimeZone, 'Unknown IANA timezone'),
  /** Messenger links, '' when not set — see `socialLinks.ts`. */
  socials: z
    .object({ max: z.string(), telegram: z.string(), whatsapp: z.string() })
    .default({ max: '', telegram: '', whatsapp: '' }),
})

export type Business = z.infer<typeof businessSchema>
export type WorkingDay = z.infer<typeof workingDaySchema>

export type OpenState =
  | { readonly status: 'open'; readonly closesAt: string }
  | { readonly status: 'closed'; readonly opensNextAt: string; readonly opensNextDay: Weekday }
  | { readonly status: 'closed-today' }

const WEEKDAY_NAMES: Readonly<Record<Weekday, string>> = {
  0: 'воскресенье',
  1: 'понедельник',
  2: 'вторник',
  3: 'среда',
  4: 'четверг',
  5: 'пятница',
  6: 'суббота',
}

export function weekdayName(weekday: Weekday): string {
  return WEEKDAY_NAMES[weekday]
}

function toMinutes(time: string): number {
  const [hours, minutes] = time.split(':')
  return Number(hours) * 60 + Number(minutes)
}

function dayFor(business: Business, weekday: Weekday): WorkingDay | undefined {
  return business.workingDays.find((day) => day.weekday === weekday)
}

function nextWeekday(weekday: Weekday): Weekday {
  return weekdaySchema.parse((weekday + 1) % 7)
}

/**
 * Pure function so it can be reasoned about without a clock.
 *
 * Read on the studio's clock. This used `now.getDay()` and `getHours()`, which
 * answer in the browser's zone: a customer seven hours east of the studio was
 * told it was closed while it was open, and on which day it would reopen.
 */
export function resolveOpenState(business: Business, now: Date): OpenState {
  const studioNow = momentInZone(now, business.timezone)
  const today = weekdaySchema.parse(studioNow.weekday)
  const nowMinutes = studioNow.minutes
  const todaySchedule = dayFor(business, today)

  if (
    todaySchedule?.opensAt !== undefined &&
    todaySchedule.closesAt !== undefined &&
    nowMinutes >= toMinutes(todaySchedule.opensAt) &&
    nowMinutes < toMinutes(todaySchedule.closesAt)
  ) {
    return { status: 'open', closesAt: todaySchedule.closesAt }
  }

  if (todaySchedule?.opensAt !== undefined && nowMinutes < toMinutes(todaySchedule.opensAt)) {
    return { status: 'closed', opensNextAt: todaySchedule.opensAt, opensNextDay: today }
  }

  let cursor = nextWeekday(today)
  for (let step = 0; step < 7; step += 1) {
    const schedule = dayFor(business, cursor)
    if (schedule?.opensAt !== undefined) {
      return { status: 'closed', opensNextAt: schedule.opensAt, opensNextDay: cursor }
    }
    cursor = nextWeekday(cursor)
  }

  return { status: 'closed-today' }
}

export function formatWorkingDay(day: WorkingDay): string {
  if (day.opensAt === undefined || day.closesAt === undefined) {
    return 'выходной'
  }
  return `${day.opensAt}–${day.closesAt}`
}

/** Today's hours on the studio's clock: "09:00–20:00" or "выходной". */
export function todaysHours(business: Business, now: Date): string {
  const today = weekdaySchema.parse(momentInZone(now, business.timezone).weekday)
  const day = dayFor(business, today)
  return day === undefined ? 'выходной' : formatWorkingDay(day)
}
